/**
 * **Grapple dominance** — who drags whom in a grapple pair (RAW Core p.69). It lives in the domain
 * because the domain's own movement context reads it: on the dominant party's `move_token` its
 * dragged partner is no obstacle (`movement.ts`, `projects/grapple-move-feel.md`). The rules
 * layer's `grapple.ts` re-exports it and owns everything else about the bind.
 */
import { GRAPPLED_STATUS, type CombatantId, type EncounterState } from "./types";

/**
 * The partner-stamped overlay marker RAW's "counts as larger" same-Size tiebreak plants on the
 * contest winner. Persistent (`until_removed`) across turns per RAW p.69's literal "until this
 * contest is repeated" — cleared only by a repeat contest or grapple end
 * (the rules layer's `grappleClearSteps`). Never read raw off a combatant's statuses — always through
 * {@link grappleDominantOf}, the one owner.
 */
export const GRAPPLE_DOMINANCE_STATUS = "Grapple Dominance";

/**
 * A grapple Size claim the carrier makes against its `partner`: it **counts as larger** than them
 * for the grapple, whatever the two Sizes say, so it is dominant and there is no same-Size contest.
 * Synthetic Muscle Netting plants it on a grapple of a same-Size or smaller target ("you count as
 * at least one Size larger"). Read only through {@link grappleSizeOrder}.
 */
export const GRAPPLE_SIZE_EDGE_STATUS = "Grapple Size Edge";

/**
 * A grapple Size claim the carrier makes against its `partner`: the pair **counts as the same
 * Size** for the grapple, so neither drags until a same-Size contest. Synthetic Muscle Netting
 * plants it on a grapple of a larger target ("you count as the same Size as the largest
 * opponent"). Read only through {@link grappleSizeOrder}.
 */
export const GRAPPLE_SIZE_PARITY_STATUS = "Grapple Size Parity";

/**
 * How `a` compares to `b` in Size **for their grapple**: `1` if `a` counts larger, `-1` smaller,
 * `0` the same, `undefined` when either is unresolvable. A {@link GRAPPLE_SIZE_EDGE_STATUS} claim
 * between the two wins, then a {@link GRAPPLE_SIZE_PARITY_STATUS} claim, then `base.size`.
 */
export function grappleSizeOrder(state: EncounterState, a: CombatantId, b: CombatantId): -1 | 0 | 1 | undefined {
  const ca = state.combatants[a];
  const cb = state.combatants[b];
  if (ca === undefined || cb === undefined) return undefined;
  const claims = (c: typeof ca, name: string, other: CombatantId) => c.statuses.some((s) => s.name === name && s.partner === other);
  if (claims(ca, GRAPPLE_SIZE_EDGE_STATUS, b)) return 1;
  if (claims(cb, GRAPPLE_SIZE_EDGE_STATUS, a)) return -1;
  if (claims(ca, GRAPPLE_SIZE_PARITY_STATUS, b) || claims(cb, GRAPPLE_SIZE_PARITY_STATUS, a)) return 0;
  return ca.base.size > cb.base.size ? 1 : ca.base.size < cb.base.size ? -1 : 0;
}

/** The live `Grappled` marker on `actor`, if any. */
export function grappledStatus(state: EncounterState, actor: string) {
  return state.combatants[actor]?.statuses.find((s) => s.name === GRAPPLED_STATUS);
}

/** The combatant `actor` is Grappled with, read off its own marker's partner-ref. */
export function grapplePartner(state: EncounterState, actor: string): CombatantId | undefined {
  return grappledStatus(state, actor)?.partner;
}

/**
 * **The one owner of "who's dominant" between a grapple pair `a`/`b`.**
 * RAW p.69: the larger party drags the smaller; on a same-Size pair,
 * either can contest at turn start and "the winner counts as larger than the loser until this
 * contest is repeated." Resolution order:
 *
 * 1. **Strictly larger** ({@link grappleSizeOrder}: `base.size`, or a Size claim) wins outright —
 *    no contest possible.
 * 2. **The same Size for the grapple** — dominance is derived *only* from a live {@link GRAPPLE_DOMINANCE_STATUS}
 *    marker: whichever of `a`/`b` carries one whose `partner` points at the other. No marker on
 *    either side (the pre-contest state) ⇒ `undefined` — neither drags, neither is Immobilized
 *    (RAW has no movement-halving here, just "no drag yet").
 * 3. Otherwise `undefined` (unresolvable `a`/`b`, or a same-Size pair with no live marker).
 *
 * Every consumer (the drag recomputer, the rules layer's `grappleImmobilizedSyncSteps`, and the dominant mover's movement context) reads dominance
 * through this function alone — never a raw `base.size` compare or a raw status lookup — so the
 * two arms of the rule (size-derived vs. contest-derived) can't drift apart across call sites.
 */
export function grappleDominantOf(
  state: EncounterState,
  a: CombatantId,
  b: CombatantId,
): CombatantId | undefined {
  const ca = state.combatants[a];
  const cb = state.combatants[b];
  const order = grappleSizeOrder(state, a, b);
  if (ca === undefined || cb === undefined || order === undefined) return undefined;
  if (order === 1) return a;
  if (order === -1) return b;
  // Equal Size — pre-contest neither is dominant; the winner of a same-Size contest is
  // recorded as whichever side carries a `Grapple Dominance` marker pointing at the other.
  if (ca.statuses.some((s) => s.name === GRAPPLE_DOMINANCE_STATUS && s.partner === b)) return a;
  if (cb.statuses.some((s) => s.name === GRAPPLE_DOMINANCE_STATUS && s.partner === a)) return b;
  return undefined;
}
